import { useDictationStore } from '@/features/dictation/dictationBubble/store/useDictationStore';
import {
  MAX_DICTATION_DURATION_MS,
  MAX_DURATION_WARNING_MS,
  WARNING_START_MS,
} from '@heidi/dictate';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

export { MAX_DICTATION_DURATION_MS, MAX_DURATION_WARNING_MS, WARNING_START_MS };

export const elapsedMsAtom = atom(0);
export const isMaxDurationApproachingAtom = atom(
  (get) => get(elapsedMsAtom) >= WARNING_START_MS
);

/**
 * Tracks elapsed dictation time, shares it via atom, and auto-stops
 * when the max duration is reached. Should be called once in the
 * parent `DictationListening` component.
 */
export const useMaxDuration = () => {
  const status = useDictationStore((s) => s.status);
  const startedAt = useDictationStore((s) => s.startedAt);
  const stop = useDictationStore((s) => s.stop);
  const setElapsedMs = useSetAtom(elapsedMsAtom);
  const elapsedMs = useAtomValue(elapsedMsAtom);
  const isMaxDurationApproaching = useAtomValue(isMaxDurationApproachingAtom);

  // Track elapsed recording time
  useEffect(() => {
    if (!startedAt || status !== 'LISTENING') {
      setElapsedMs(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startedAt.getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt, status, setElapsedMs]);

  // Auto-stop when max duration is reached
  useEffect(() => {
    if (elapsedMs >= MAX_DICTATION_DURATION_MS && status === 'LISTENING') {
      void stop();
    }
  }, [elapsedMs, status, stop]);

  return { elapsedMs, isMaxDurationApproaching };
};
